import React, { FC } from "react";
import Typography from "@mui/material/Typography";
import Selection from "../Selection/Selection";
import Paper from "@mui/material/Paper";
import Grow from "@mui/material/Grow";
import Radio from "@mui/material/Radio";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import ReusableAccordian from "../ReusableAccordion";
import PleaseNote from "../PleaseNote";
import { RowLayout, ColLayout } from "../../util/accordianStyles";
import { styled } from "@mui/material/styles";
import useConfigSelection from "./useConfigSelection";
import { StyledDivCol } from "../dealerZone/Cutsheet/CutsheetEdits";
import { useIsReadOnlyContextProviderContext } from "../../context/IsReadOnlyContext";

const StyledPaper = styled(Paper)({
  "&:hover": {
    cursor: "pointer",
  },
});

const StyledDivRow = styled("div")({
  display: "flex",
  justifyContent: "space-around",
  alignItems: " center",
  width: "100%",
});

const StyledTypography = styled(Typography)({
  color: "red",
  paddingTop: 10
});

export const noAccess = [
  "2L OR 2R",
  "2L2R",
  "4L OR 4R",
  "6L OR 6R",
  "4L2R OR 2L4R",
  "8L OR 8R",
  "6L2R OR 2L6R",
  "4L4R",
];
export const specifyPrimary = [
  "3L1R",
  "1L3R",
  "5L1R",
  "1L5R",
  "3L3R",
  "7L1R",
  "1L7R",
  "5L3R",
  "3L5R",
  "2L2R",
  "4L2R",
  "2L4R",
  "6L2R",
  "2L6R",
  "4L4R",
];

export const evenEven = ["2L2R", "4L2R OR 2L4R", "6L2R OR 2L6R", "4L4R"];

interface ConfigurationProps {
  bifoldType: string;
  hideHandlesWithEvenConfigs: (access: boolean) => void;
}

const ConfigurationAccordian: FC<ConfigurationProps> = ({
  bifoldType,
  hideHandlesWithEvenConfigs
}) => {
  const {
    handleChange,
    handleConfigClick,
    selectedConfig,
    singleImage,
    configurationOptions,
    configuration,
    primaryDoor,
    netWidth,
    exactConfig,
    stileCategory,
    imageLeft,
    imageRight,
    handleChangeSboltExt,
    withExtSbolt,
    isPico,
    isAlum,
    material
  } = useConfigSelection(bifoldType, hideHandlesWithEvenConfigs);
  const { readOnly } = useIsReadOnlyContextProviderContext();

  return (
    <ReusableAccordian
      accordionName="configuration"
      heading="Configuration"
      secondaryHeading="Select a configuration"
      required5={true}
    >
      {!configurationOptions || configurationOptions.length === 0 ? (
        <PleaseNote notes="Enter a Frame Width to view Configuration Options" />
      ) : null}
      {configurationOptions && configurationOptions.length > 0 ? (
        <>
          <ColLayout>
            <Selection
              chosenConfig={configuration}
              selectedConfig={selectedConfig}
              typeRequired={"config"}
              displayedConfig={
                configurationOptions.length ? configurationOptions : []
              }
              netWidth={netWidth}
            />
            {isAlum && withExtSbolt === "No" ? (
              <StyledTypography variant="caption">
                * No access from exterior
              </StyledTypography>
            ) : isAlum ? null : (
              (noAccess.includes(configuration || "") ||
                bifoldType === "window") && (
                <RowLayout>
                  {bifoldType === "window" ? (
                    <StyledTypography variant="caption">
                      * No access from exterior
                    </StyledTypography>
                  ) : !stileCategory &&
                    material === "Clad Exterior/Wood Interior" ? (
                    <StyledTypography variant="caption">
                      Access from exterior only possible with{" "}
                      <img
                        alt="pico txt"
                        src={`https://res.cloudinary.com/ag-millworks/image/upload/Pico_Text/svg/pico_red_v2.svg`}
                        style={{ width: 40, height: "auto" }}
                      />{" "}
                      stiles
                    </StyledTypography>
                  ) : stileCategory !== "Pico" ? (
                    <StyledTypography variant="caption">
                      * No access from exterior
                      {bifoldType !== "window" && " with this configuration"}
                    </StyledTypography>
                  ) : null}
                </RowLayout>
              )
            )}
            {/* Primary Door option is only for both odd sided non Pico configs or for Pico units that are both even/odd sided */}
            {(bifoldType === "window" || !isPico || withExtSbolt !== "Yes") && evenEven.includes(configuration || "")
              ? null
              : configuration &&
                specifyPrimary.includes(configuration?.split(" OR")[0]) && (
                  <StyledDivRow>
                    <Typography style={{ fontWeight: "bold", minWidth: 170 }}>
                      Primary Door:
                      <span style={{ color: "red", fontSize: 22 }}>*</span>
                    </Typography>
                    <FormControl component="fieldset">
                      <FormControlLabel
                        value="end"
                        control={
                          <Radio
                            color="primary"
                            checked={primaryDoor === "Left"}
                            onChange={handleChange}
                            value="Left"
                            readOnly={readOnly}
                          />
                        }
                        label="Left"
                        labelPlacement="end"
                      />
                      <FormControlLabel
                        value="end"
                        control={
                          <Radio
                            color="primary"
                            checked={primaryDoor === "Right"}
                            onChange={handleChange}
                            value="Right"
                            readOnly={readOnly}
                          />
                        }
                        label="Right"
                        labelPlacement="end"
                      />
                    </FormControl>
                  </StyledDivRow>
                )}
            {/* Exterior shootbolt option is only for non window aluminum or pico units that are  even*/}
            {bifoldType !== "window" && (isAlum || isPico) &&
              noAccess.includes(configuration || "") && (
                <StyledDivRow style={{ marginTop: 20 }}>
                  <StyledDivCol style={{ minWidth: 170 }}>
                    <Typography style={{ fontWeight: "bold" }}>
                      Exterior shootbolt
                      <span style={{ color: "red", fontSize: 22 }}>*</span>
                    </Typography>
                    <Typography style={{ fontWeight: "bold" }}>
                      handle with key:
                    </Typography>
                  </StyledDivCol>

                  <FormControl component="fieldset">
                    <FormControlLabel
                      value="end"
                      control={
                        <Radio
                          color="primary"
                          checked={withExtSbolt === "No"}
                          onChange={handleChangeSboltExt}
                          value="No"
                          disabled={readOnly}
                        />
                      }
                      label="No"
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="end"
                      control={
                        <Radio
                          color="primary"
                          checked={withExtSbolt === "Yes"}
                          onChange={handleChangeSboltExt}
                          value="Yes"
                          disabled={readOnly}
                        />
                      }
                      label="Yes"
                      labelPlacement="end"
                    />
                  </FormControl>
                </StyledDivRow>
              )}
          </ColLayout>
          {imageLeft && configuration && (
            <ColLayout style={{ paddingLeft: 40 }}>
              <Typography variant="caption">
                * As viewed from exterior
              </Typography>
              {!exactConfig ? (
                <Typography variant="body1">
                  Choose a specific configuration
                </Typography>
              ) : (
                <Typography style={{ color: "#04bff3" }} variant="h6">
                  You selected {exactConfig}
                </Typography>
              )}
              <RowLayout>
                <Grow in={true}>
                  <StyledPaper
                    style={{
                      marginRight: 20,
                      border:
                        configuration.split("OR")[0].trim() === exactConfig
                          ? "3px solid #04bff3"
                          : "",
                    }}
                    elevation={3}
                  >
                    {!singleImage ? (
                      <img
                        onClick={() => readOnly ? null : handleConfigClick("left")}
                        src={imageLeft}
                        alt="config door"
                      />
                    ) : (
                      <img src={imageLeft} alt="config door" />
                    )}
                  </StyledPaper>
                </Grow>

                {imageRight && (
                  <Grow in={true}>
                    <StyledPaper
                      style={{
                        border:
                          configuration.split("OR")[1].trim() === exactConfig
                            ? "3px solid #04bff3"
                            : "",
                      }}
                      elevation={3}
                    >
                      <img
                        onClick={() => readOnly ? null : handleConfigClick("right")}
                        src={imageRight}
                        alt="config door 2"
                      />
                    </StyledPaper>
                  </Grow>
                )}
              </RowLayout>
            </ColLayout>
          )}
        </>
      ) : null}
    </ReusableAccordian>
  );
};

export default ConfigurationAccordian;
